import { FeatureWrapper, FlagWrapper, useFeatureContext } from '@context'
import { useQueryParam } from '@hooks'
import theme from '@root/styles/theme'
import cn from 'classnames'
import { AnimatePresence, LayoutGroup, motion, useIsPresent } from 'framer-motion'
import React, { isValidElement, useEffect, useId } from 'react'
import { FaCircle } from 'react-icons/fa'
import styled, { css } from 'styled-components'

interface TabsProps {
  queryKey?: string
  children: React.ReactNode
  wrapperStyles?: React.CSSProperties
  /** When true, tab header is hidden if only one tab is visible after feature/flag filtering. */
  hideTabsWhenSingle?: boolean
  /** Pill toolbar with underline tabs and the active tab's actions on the same row. */
  toolbar?: boolean
  /** Keep the tab panel filling the available height while its content scrolls. */
  fillHeight?: boolean
}

const isTransparentTabsProxy = (componentType: unknown): boolean =>
  Boolean(
    componentType &&
      typeof componentType !== 'string' &&
      (componentType as { tabsProxy?: boolean }).tabsProxy === true
  )

const collectTabs = (
  children: React.ReactNode,
  features: ReturnType<typeof useFeatureContext>['features'],
  flags: ReturnType<typeof useFeatureContext>['flags']
): Array<React.ReactElement<TabProps>> => {
  return React.Children.toArray(children).flatMap(child => {
    if (!isValidElement(child)) {
      return []
    }

    if (child.type === Tab) {
      return [child as React.ReactElement<TabProps>]
    }

    const props =
      typeof child.props === 'object' && child.props !== null
        ? (child.props as {
            children?: React.ReactNode
            alternativeContent?: React.ReactNode | (() => React.ReactNode)
            feature?: string
            flag?: string
          })
        : {}

    if (child.type === FeatureWrapper) {
      const featureKey = props.feature
      const featureState = featureKey ? features[featureKey] : undefined

      if (!featureState || featureState.enabled) {
        return collectTabs(props.children, features, flags)
      }

      if (
        featureState.blocked &&
        props.alternativeContent &&
        typeof props.alternativeContent !== 'function'
      ) {
        return collectTabs(props.alternativeContent, features, flags)
      }

      return []
    }

    if (child.type === FlagWrapper) {
      const flagKey = props.flag
      const isEnabled = !flagKey || flags[flagKey]?.enabled !== false

      if (isEnabled) {
        return collectTabs(props.children, features, flags)
      }

      const alternativeContent =
        typeof props.alternativeContent === 'function'
          ? props.alternativeContent()
          : props.alternativeContent

      return collectTabs(alternativeContent, features, flags)
    }

    if (child.type === React.Fragment || isTransparentTabsProxy(child.type)) {
      return collectTabs(props.children, features, flags)
    }

    return []
  })
}

const getTabValue = (rawKey: React.Key | null | undefined, fallbackIndex: number): string => {
  if (rawKey === null || rawKey === undefined) {
    return `tab-${fallbackIndex}`
  }

  // React prefixes element keys internally (e.g. ".$overview"). Strip it for URL/query usage.
  return String(rawKey).replace(/^\.\$/, '')
}

export const Tabs: React.FC<TabsProps> = ({
  queryKey = 'tab',
  children: _children,
  wrapperStyles,
  hideTabsWhenSingle = false,
  toolbar = false,
  fillHeight = false
}) => {
  const [active, setActive] = useQueryParam<string>(queryKey)
  const isPresent = useIsPresent()
  const { features, flags } = useFeatureContext()
  const tabsInstanceId = useId()
  // Keep motion IDs instance-scoped so parallel mounted Tabs (e.g. during exits)
  // do not steal each other's shared-layout animation.
  const tabIndicatorLayoutId = `tabs-active-indicator-${queryKey}-${tabsInstanceId}`
  const tabLayoutGroupId = `tabs-layout-group-${queryKey}-${tabsInstanceId}`

  const children = collectTabs(_children, features, flags)
  const tabs = children.map((tab, index) => ({
    tab,
    tabValue: getTabValue(tab.key, index)
  }))

  // If any of the tabs is active, set the first tab as active
  useEffect(() => {
    /*
    This check is needed to avoid 
    setting the state on unmounted component.
    For example if used under AnimatePresence two of the Tabs
    would exist at the same time during exit animation which
    would cause this effect to run on the unmounted one --> 
    the tabs would flicker a lot. 
    */
    if (!isPresent) {
      return
    }
    if (!tabs.some(({ tabValue }) => tabValue === active) && tabs[0]) {
      setActive(tabs[0].tabValue as any)
    }
  }, [active, tabs, setActive, isPresent])

  const shouldHideTabHeader = hideTabsWhenSingle && tabs.length <= 1
  const activeTab = tabs.find(({ tabValue }) => tabValue === active)

  const renderTabButton = (tab: React.ReactElement<TabProps>, tabValue: string) => (
    <StyledTabButton
      key={`tab-${tabValue}`}
      className={cn({ isSelected: tabValue === active })}
      onClick={() => setActive(tabValue as any)}
    >
      {tab.props.icon} {tab.props.name} {<NotificationIcon type={tab.props.notification_dot} />}
      {tabValue === active && <ActiveTabIndicator layoutId={tabIndicatorLayoutId} />}
    </StyledTabButton>
  )

  return (
    <StyledTabsWrapper
      id={`tabs-wrapper-${queryKey}`}
      className="tabs-wrapper"
      $fill={fillHeight}
      style={{ ...(wrapperStyles ?? {}) }}
    >
      {!shouldHideTabHeader &&
        (toolbar ? (
          <ToolbarRow>
            <ToolbarTabs id={`tabs-header-${queryKey}`} className="tabs-header">
              <LayoutGroup id={tabLayoutGroupId}>
                {tabs.map(({ tab, tabValue }) => renderTabButton(tab, tabValue))}
              </LayoutGroup>
            </ToolbarTabs>
            <ToolbarActions>{activeTab?.tab.props.actions}</ToolbarActions>
          </ToolbarRow>
        ) : (
          <TabsScrollable>
            <StyledTabs id={`tabs-header-${queryKey}`} className="tabs-header">
              <LayoutGroup id={tabLayoutGroupId}>
                {tabs.map(({ tab, tabValue }) => renderTabButton(tab, tabValue))}
              </LayoutGroup>
            </StyledTabs>
          </TabsScrollable>
        ))}

      <AnimatePresence mode="wait">
        {tabs.filter(({ tabValue }) => tabValue === active).map(({ tab }) => tab)}
      </AnimatePresence>
    </StyledTabsWrapper>
  )
}

interface TabProps {
  name: string
  icon?: React.ReactNode
  notification_dot?: NotificationUrgency
  actions?: React.ReactNode
  children: React.ReactNode
}

export const Tab: React.FC<TabProps> = ({ children }) => {
  return <StyledTab className="tabs-tab">{children}</StyledTab>
}

const BORDER_WIDTH = '1px'

const StyledTabButton = styled.button`
  position: relative;
  border: none;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  outline: none !important;
  background: transparent;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.metalGray};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  cursor: pointer;
`

const ActiveTabIndicator = styled(motion.div).attrs({
  transition: { type: 'spring', stiffness: 500, damping: 40, mass: 0.6 }
})`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -${BORDER_WIDTH};
  height: 2px;
  background-color: ${({ theme }) => theme.colors.primary};
`

const ToolbarRow = styled.div`
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  box-sizing: border-box;
  width: 100%;
  max-width: 960px;
  min-height: ${({ theme }) => theme.spacing.md * 2 + theme.spacing.sm * 2 + theme.fontSize.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  padding: 0 ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;

  ${ActiveTabIndicator} {
    bottom: 0;
  }
`

const ToolbarTabs = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  overflow-x: auto;
  white-space: nowrap;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${StyledTabButton} {
    color: ${({ theme }) => theme.colors.iconGray};

    &.isSelected {
      color: ${({ theme }) => theme.colors.metalGray};
    }
  }
`

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-height: ${({ theme }) => theme.spacing.md * 2 + theme.spacing.sm * 2 + theme.fontSize.md}rem;
`

const TabsScrollable = styled.div`
  overflow-x: scroll;
  overflow-y: visible;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const StyledTabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  white-space: nowrap;

  border-bottom: ${BORDER_WIDTH} solid ${({ theme }) => theme.colors.neutralGray};
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;
`

const StyledTabsWrapper = styled.div<{ $fill?: boolean }>`
  display: flex;
  flex-direction: column;

  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      min-height: 0;
      overflow: hidden;

      > .tabs-tab {
        min-height: 0;
        overflow: auto;
      }
    `}
`

const StyledTab = styled(motion.div).attrs({
  initial: { opacity: 0, y: -5 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 5 }
})`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

export const enum NotificationUrgency {
  URGENT = 'urgent',
  NON_URGENT = 'non_urgent'
}

interface NotificationIconProps {
  type: NotificationUrgency
}

export const NotificationIcon: React.FC<NotificationIconProps> = ({ type }) => {
  const style = {
    width: `${theme.spacing.sm}rem`,
    height: `${theme.spacing.sm}rem`
  }

  const Urgent = () => <FaCircle color={theme.colors.error} style={style} />
  const NonUrgent = () => <FaCircle color={theme.colors.primary} style={style} />

  const icon: Record<NotificationUrgency, React.ReactNode> = {
    urgent: <Urgent />,
    non_urgent: <NonUrgent />
  }

  return icon[type]
}
